import React from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import {
    CreateGeneralAgreementDocument,
    CreateGeneralAgreementPositionDocument,
    ListGeneralAgreementsDocument,
    SortOrder,
    Rahmenvertrag,
} from '~/gql/ucpw/graphql';
import { selects } from '~/meta/data/selects.gql';
import { useContextAndRefetchQueries } from '~/hooks/useContextAndRefetchQueries';
import {
    agreementNameFromFile,
    assertFileSize,
    computeValidityDates,
    decodeCsvBytes,
    parseCsv,
    prepareImport,
    CsvStructuralError,
    FALLBACK_UNIT,
    PreparedPosition,
    RowIssue,
} from '~/pages/master-data/ui/GeneralAgreements/lib/csvImport';
import log from '~/log';

export type ImportPhase = 'select' | 'preview' | 'processing' | 'error' | 'report';

export interface ImportReport {
    bezeichnung: string;
    /** Non-blank data rows in the file. */
    totalRows: number;
    /** Positions successfully created. */
    imported: number;
    /** Rows skipped for lack of a usable price (empty or unreadable cell). */
    skipped: number;
    /** Positions that failed to insert. */
    failed: number;
    /** Whether the filename was truncated to fit the 45-char bezeichnung. */
    nameTruncated: boolean;
    /** Whether an RV was actually created (false when 0 importable rows). */
    agreementCreated: boolean;
    issues: RowIssue[];
}

/**
 * Result of the dry-run analysis shown before any data is written. Mirrors the
 * numbers in {@link ImportReport} but framed as "will be imported".
 */
export interface ImportPreview {
    /** RV bezeichnung derived from the filename (truncated to 45). */
    bezeichnung: string;
    nameTruncated: boolean;
    /** Computed validity range (AWSDate YYYY-MM-DD). */
    gueltigAb: string;
    gueltigBis: string;
    /** Non-blank data rows in the file. */
    totalRows: number;
    /** Rows that would be imported as positions. */
    importable: number;
    /** Rows that would be skipped (empty or unreadable price). */
    skipped: number;
    issues: RowIssue[];
    /** True when the `Sonstiges` master data is missing → import is blocked. */
    stammdatenMissing: boolean;
}

/** Everything the confirm step needs, captured during {@link analyze}. */
interface Analysis {
    positions: PreparedPosition[];
    unitMap: Record<string, number>;
    bezeichnung: string;
    nameTruncated: boolean;
    totalRows: number;
    skipped: number;
    rowIssues: RowIssue[];
    positionIssues: RowIssue[];
    gueltigAb: string;
    gueltigBis: string;
}

const LIST_VARIABLES = {
    limit: 500,
    orderBy: [{ isStandard: SortOrder.Desc }, { bezeichnung: SortOrder.Asc }],
};

const context = { clientName: 'ucpw' };

/**
 * Orchestrates the client-side CSV bulk-import (ADR 0001): parse + decode the
 * file, build the `Einheit → leistungId` map from the seeded `Sonstiges` rows,
 * create the Rahmenvertrag, then create one position per importable row.
 * Best-effort, no transaction/rollback.
 */
export const useAgreementCsvImport = ({
    onImported,
}: {
    onImported?: (rv: Rahmenvertrag) => void;
} = {}) => {
    const [phase, setPhase] = React.useState<ImportPhase>('select');
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [progress, setProgress] = React.useState<{ done: number; total: number }>({
        done: 0,
        total: 0,
    });
    const [report, setReport] = React.useState<ImportReport | null>(null);
    const [preview, setPreview] = React.useState<ImportPreview | null>(null);
    const [analyzing, setAnalyzing] = React.useState(false);

    // Analysis result kept between the dry-run (analyze) and the actual write
    // (confirmImport). A ref, not state: it never needs to trigger a re-render.
    const analysisRef = React.useRef<Analysis | null>(null);

    const refetchConfig = useContextAndRefetchQueries(ListGeneralAgreementsDocument, LIST_VARIABLES);

    const client = useApolloClient();
    const [createAgreement] = useMutation(CreateGeneralAgreementDocument, refetchConfig);
    const [createPosition] = useMutation(CreateGeneralAgreementPositionDocument, { context });

    const reset = React.useCallback(() => {
        setPhase('select');
        setErrorMessage('');
        setProgress({ done: 0, total: 0 });
        setReport(null);
        setPreview(null);
        setAnalyzing(false);
        analysisRef.current = null;
    }, []);

    /** Discard the dry-run and return to file selection (e.g. to pick a fixed CSV). */
    const backToSelect = React.useCallback(() => {
        analysisRef.current = null;
        setPreview(null);
        setErrorMessage('');
        setPhase('select');
    }, []);

    /** Build a map of canonical Einheit → leistungId from the `Sonstiges` rows. */
    const loadSonstigesMap = React.useCallback(async (): Promise<Record<string, number>> => {
        const { data } = await client.query({
            query: selects.units,
            variables: { limit: 100, filter: { bezeichnungPrefix: 'Sonstiges' } },
            context,
            fetchPolicy: 'network-only',
        });
        const items: any[] = data?.items?.items || [];
        const map: Record<string, number> = {};
        for (const item of items) {
            if (item?.bezeichnung === 'Sonstiges' && item?.einheit != null && item?.value != null) {
                map[item.einheit] = item.value;
            }
        }
        return map;
    }, [client]);

    /**
     * Dry-run: decode + parse + prepare the file and load the `Sonstiges` map,
     * WITHOUT writing anything. Produces an {@link ImportPreview} the user
     * confirms (or rejects) before the actual import.
     */
    const analyze = React.useCallback(
        async (file: File) => {
            setErrorMessage('');
            setAnalyzing(true);
            try {
                // 1. Decode + parse (hard gate on structural errors).
                let rows;
                try {
                    assertFileSize(file.size);
                    const buffer = await file.arrayBuffer();
                    const text = decodeCsvBytes(buffer);
                    rows = parseCsv(text);
                } catch (e) {
                    const message =
                        e instanceof CsvStructuralError
                            ? e.message
                            : 'Die Datei konnte nicht als CSV gelesen werden.';
                    setErrorMessage(message);
                    setPhase('error');
                    return;
                }

                // 2. Prepare positions + collect row-level issues.
                const { positions, rowIssues, totalRows } = prepareImport(rows);
                const { bezeichnung, truncated: nameTruncated } = agreementNameFromFile(file.name);
                const positionIssues = positions.flatMap((p) => p.issues);
                const skipped = rowIssues.filter(
                    (i) => i.category === 'priceEmpty' || i.category === 'priceUnparseable'
                ).length;
                const { gueltigAb, gueltigBis } = computeValidityDates();

                // 3. Load the Sonstiges map up front, so the preview can warn
                // before anything is written (and the map is reused at confirm).
                let unitMap: Record<string, number> = {};
                let stammdatenMissing = false;
                if (positions.length > 0) {
                    try {
                        unitMap = await loadSonstigesMap();
                    } catch (e) {
                        log.error('csvImport.loadSonstigesMap', e);
                        setErrorMessage(
                            'Die Leistungs-Stammdaten konnten nicht geladen werden. Bitte versuchen Sie es erneut.'
                        );
                        setPhase('error');
                        return;
                    }
                    stammdatenMissing = Object.keys(unitMap).length === 0;
                }

                analysisRef.current = {
                    positions,
                    unitMap,
                    bezeichnung,
                    nameTruncated,
                    totalRows,
                    skipped,
                    rowIssues,
                    positionIssues,
                    gueltigAb,
                    gueltigBis,
                };

                setPreview({
                    bezeichnung,
                    nameTruncated,
                    gueltigAb,
                    gueltigBis,
                    totalRows,
                    importable: positions.length,
                    skipped,
                    issues: sortIssues([...rowIssues, ...positionIssues]),
                    stammdatenMissing,
                });
                setPhase('preview');
            } finally {
                setAnalyzing(false);
            }
        },
        [loadSonstigesMap]
    );

    /**
     * Commit the analyzed import: create the RV, then one position per row
     * (best-effort, ADR 0001). Uses the analysis captured by {@link analyze}.
     */
    const confirmImport = React.useCallback(async () => {
        const analysis = analysisRef.current;
        // Guarded by the preview UI; bail defensively if there is nothing to do.
        if (!analysis || analysis.positions.length === 0) return;
        if (Object.keys(analysis.unitMap).length === 0) return;

        const {
            positions,
            unitMap,
            bezeichnung,
            nameTruncated,
            totalRows,
            skipped,
            rowIssues,
            positionIssues,
            gueltigAb,
            gueltigBis,
        } = analysis;

        setPhase('processing');
        setErrorMessage('');
        setProgress({ done: 0, total: positions.length });

        // 1. Create the Rahmenvertrag.
        let rahmenvertragId = 0;
        let createdRv: Rahmenvertrag | undefined;
        try {
            const res = await createAgreement({
                variables: { data: { bezeichnung, gueltigAb, gueltigBis, isStandard: false } },
            });
            const payload = (res?.data as any)?.item;
            if (payload?.error || !payload?.item?.id) {
                throw new Error(payload?.error?.message || 'Rahmenvertrag konnte nicht angelegt werden.');
            }
            createdRv = payload.item as Rahmenvertrag;
            rahmenvertragId = payload.item.id;
        } catch (e: any) {
            log.error('csvImport.createAgreement', e);
            setErrorMessage(
                e?.message ||
                    'Der Rahmenvertrag konnte nicht angelegt werden. Es wurde nichts importiert.'
            );
            setPhase('error');
            return;
        }

        // 2. Create positions sequentially (best-effort).
        const insertIssues: RowIssue[] = [];
        let imported = 0;
        for (let i = 0; i < positions.length; i++) {
            const pos = positions[i];
            const leistungId = unitMap[pos.einheit] ?? unitMap[FALLBACK_UNIT];
            try {
                if (leistungId == null) {
                    throw new Error(`Keine Leistung für Einheit "${pos.einheit}" gefunden.`);
                }
                const res = await createPosition({
                    variables: {
                        data: {
                            rahmenvertragId,
                            leistungId,
                            nr: pos.nr,
                            beschreibung: pos.beschreibung,
                            details: pos.details,
                            preisProEinheit: pos.preisProEinheit,
                        },
                    },
                });
                const payload = (res?.data as any)?.item;
                if (payload?.error || !payload?.item?.id) {
                    throw new Error(payload?.error?.message || 'insert failed');
                }
                imported++;
            } catch (e) {
                log.error('csvImport.createPosition', pos.line, e);
                insertIssues.push({
                    category: 'positionInsertFailed',
                    line: pos.line,
                    value: pos.nr || pos.beschreibung,
                    nr: pos.nr,
                });
            }
            setProgress({ done: i + 1, total: positions.length });
        }

        // 3. Report + auto-select the new RV.
        setReport({
            bezeichnung,
            totalRows,
            imported,
            skipped,
            failed: insertIssues.length,
            nameTruncated,
            agreementCreated: true,
            issues: sortIssues([...rowIssues, ...positionIssues, ...insertIssues]),
        });
        setPhase('report');

        if (createdRv) {
            onImported?.(createdRv);
        }
    }, [createAgreement, createPosition, onImported]);

    return {
        phase,
        errorMessage,
        progress,
        report,
        preview,
        analyzing,
        analyze,
        confirmImport,
        backToSelect,
        reset,
    };
};

function sortIssues(issues: RowIssue[]): RowIssue[] {
    return [...issues].sort((a, b) => a.line - b.line);
}
